import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
//Helpers
import { gen2Request } from 'src/services/gen_2_http_reqs';
import { isPlusUni } from '../interface_helpers';
import { MODULES } from 'src/constants';
import { onSettingsResponse } from '../device_settings_helpers';
import { isProSensorAddon } from './addon_support_helpers';
import { hasSwitchAddon } from './addon_helpers';
//Types
import type { Device } from 'src/types/Device';
import type { SettingRequest } from 'src/types/requests/settings/setting';
import type { SensorScanComponent, SensorScanResult, SensorType } from 'src/types/Peripherals';

/**
 * Returns the peripheral types that a device supports.
 * @param {Device} device - The device to get the peripheral types for.
 * @return {Object} - An object with the peripheral types as keys and the peripheral names as values.
 */
export const getPeripheralsTypes = (
	device: Device,
): {
	[key in SensorType]?: string;
} => {
	if (hasSwitchAddon(device)) {
		return peripheralsPro3EMTypes;
	} else if (isProSensorAddon(device)) {
		return peripheralsProAddonTypes;
	} else if (isPlusUni(device.type)) {
		return peripheralsPlusUniTypes;
	} else if (MODULES.includes(device.type)) {
		return peripheralsModulesTypes;
	}
	return peripheralsPlusTypes;
};

/**
 * Scans for peripherals of a given type on a device.
 * If the given sensorType is 'ds18b20', it will use the one wire peripheral scan.
 * Otherwise, it will use the peripheral scan.
 * @param {Device} device - The device to scan the peripherals for.
 * @param {SensorType} sensorType - The type of sensor to scan the peripherals for.
 * @return {Promise<SensorScanResult>} - A promise that resolves to an object with the error, peripherals, and sensorType.
 */
export const scanPeripheralByType = async (
	device: Device,
	sensorType: SensorType,
): Promise<SensorScanResult> => {
	return sensorType === 'ds18b20'
		? await scanOnewireSensors(device, sensorType)
		: await scanPeripheralSensors(device, sensorType);
};

/**
 * Adds a peripheral to a device's addon.
 * @param {Device} device - The device to add the peripheral to.
 * @param {SensorType} selectedSensorType - The type of sensor to add.
 * @param {SensorScanComponent} selectedSensorComponent - The sensor component to add.
 * @return {Promise<SettingRequest>} - A promise that resolves to an object with the result of the request.
 */
export const addAddonPeripheral = async (
	device: Device,
	selectedSensorType: SensorType,
	selectedSensorComponent: SensorScanComponent,
) => {
	const requestPrefix = hasSwitchAddon(device) ? 'prooutputaddon' : 'sensoraddon';
	const params: { type: SensorType; attrs?: { addr?: string; io?: number } } = {
		type: selectedSensorType,
	};
	if (selectedSensorType === 'ds18b20') {
		params.attrs = { addr: selectedSensorComponent?.addr };
	}
	if (selectedSensorComponent?.io != null) {
		params.attrs = { ...(params?.attrs || {}), io: selectedSensorComponent.io };
	}
	const res: SettingRequest = await gen2Request(device, `${requestPrefix}.addperipheral`, params);
	if (res?.isok) {
		res.restart_required = true;
	}
	onSettingsResponse(res, device, { danger: res?.errors?.message });
	return res;
};

/**
 * Returns a formatted string for a peripheral type.
 * @param {string} item - The peripheral type to format.
 * @returns {string} - A formatted string for the peripheral type.
 */
export const getPeripheralFormat = (item: string) => {
	const $format = get(format);
	return $format(item, {
		default: item.split('_')?.[2],
	});
};

/**
 * Validates a one wire peripheral scan for a device.
 * @param {Device} device - The device to validate the one wire peripheral scan for.
 * @param {SensorType} sensorType - The type of sensor to validate the one wire peripheral scan for.
 * @returns {Promise<SensorScanResult>} - A promise that resolves to an object with the error, peripherals, and sensorType.
 */
const scanOnewireSensors = async (device: Device, sensorType: SensorType): Promise<SensorScanResult> => {
	let error = '';
	let disabled = false;
	const $format = get(format);
	const peripheralsTypes = getPeripheralsTypes(device);
	const res: SettingRequest = await gen2Request(device, 'sensoraddon.onewirescan', {});
	let peripherals: SensorScanComponent[] = null;
	if (res?.isok) {
		peripherals = res?.data?.devices || res?.devices;
		if (!peripherals?.length) {
			error = $format('err_peripherals_not_found', {
				values: {
					peripheral_name: getPeripheralFormat(peripheralsTypes[sensorType]),
				},
				default: getPeripheralFormat(peripheralsTypes[sensorType]) + ' peripherals not found.',
			});
		} else {
			disabled = peripherals?.every((peripheral) => peripheral?.component != null);
		}
	}
	error = handlePeripheralError(res, error);
	disabled = error !== '' || disabled;
	return { error, peripherals, sensorType, disabled };
};

/**
 * Scans for peripherals of a given type on a device.
 * @param {Device} device - The device to scan peripherals for.
 * @param {SensorType} sensorType - The type of sensor to scan for.
 * @returns {Promise<SensorScanResult>} - A promise that resolves to an object with the error, peripherals, sensorType, and disabled.
 */
const scanPeripheralSensors = async (
	device: Device,
	sensorType: SensorType,
): Promise<SensorScanResult> => {
	const $format = get(format);
	const requestPrefix = hasSwitchAddon(device) ? 'prooutputaddon' : 'sensoraddon';
	const peripheralsTypes = getPeripheralsTypes(device);
	const res: SettingRequest = await gen2Request(device, `${requestPrefix}.getperipherals`, {});

	let error = '';
	let disabled = false;
	let peripherals: SensorScanComponent[] = null;

	if (res?.isok && res?.data?.[sensorType]) {
		peripherals = Object.values(res?.data?.[sensorType] || {});
		if (isProSensorAddon(device) && sensorType !== 'digital_out') {
			if (sensorType == 'dht22') {
				peripherals = [...peripherals, ...Object.values(res?.data?.['ds18b20'] || {})];
			} else if (sensorType == 'voltmeter') {
				peripherals = [...peripherals, ...Object.values(res?.data?.['analog_in'] || {})];
			} else if (sensorType == 'analog_in') {
				peripherals = [...peripherals, ...Object.values(res?.data?.['voltmeter'] || {})];
			}
			disabled =
				peripherals.find((p) => p.io === 0) != null &&
				peripherals.find((p) => p.io === 1) != null;
		} else if (Object.keys(res.data[sensorType]).length) {
			error = $format('err_peripheral_already_exists', {
				values: {
					peripheral: getPeripheralFormat(peripheralsTypes[sensorType]),
				},
				default: 'Resource {peripheral} already exists!',
			});
		} else if (sensorType == 'dht22' && Object.keys(res.data.ds18b20).length) {
			error = $format('err_cant_add_peripheral', {
				values: {
					peripheral: getPeripheralFormat(peripheralsTypes['dht22']),
					peripheral_input: getPeripheralFormat(peripheralsTypes['ds18b20']),
				},
				default: 'Can not add {peripheral}. {peripheral_input} already added!',
			});
		} else if (
			(sensorType === 'voltmeter' && Object.keys(res.data.analog_in).length) ||
			(sensorType === 'analog_in' && Object.keys(res.data.voltmeter).length)
		) {
			error = $format('err_cant_add_peripheral', {
				values: {
					peripheral: getPeripheralFormat(peripheralsTypes[sensorType]),
					peripheral_input: getPeripheralFormat(
						peripheralsTypes[sensorType === 'voltmeter' ? 'analog_in' : 'voltmeter'],
					),
				},
				default: 'Can not add {peripheral}. {peripheral_input} already added!',
			});
		}
	}
	error = handlePeripheralError(res, error);
	disabled = error !== '' || disabled;
	return { error, peripherals, sensorType, disabled };
};

/**
 * Handles peripheral error from SettingRequest and returns a formatted error string.
 * If the error is due to resource unavailability, it will return a specific error message.
 * Otherwise, it will return the error message from the SettingRequest.
 * @param {SettingRequest} res - The SettingRequest response.
 * @param {string} error - The error string to be formatted.
 * @returns {string} The formatted error string.
 */
const handlePeripheralError = (res: SettingRequest, error: string): string => {
	if (!res?.isok) {
		const $format = get(format);
		const deviceError = res?.errors || res?.error?.device_error;
		if (deviceError?.code == -114) {
			error = $format('err_peripheral_resource_unavailable2', {
				default:
					'Scanning for Temperature (DS18B20) peripherals is not available when DHT22 peripheral is enabled.',
			});
		} else {
			error =
				deviceError?.message ||
				$format('err_getting_peripherals', {
					default: 'Failed to get peripherals, please try again!',
				});
		}
	}
	return error;
};

const peripheralsPlusTypes = {
	dht22: 'addon_peripheral_dht22',
	ds18b20: 'addon_peripheral_ds18b20',
	digital_in: 'addon_peripheral_digital_in',
	analog_in: 'addon_peripheral_analog_in',
	voltmeter: 'addon_peripheral_voltmeter',
};

const peripheralsPlusUniTypes = {
	dht22: 'addon_peripheral_dht22',
	ds18b20: 'addon_peripheral_ds18b20',
	analog_in: 'addon_peripheral_analog_in',
	voltmeter: 'addon_peripheral_voltmeter',
};

const peripheralsModulesTypes = {
	dht22: 'addon_peripheral_dht22',
	ds18b20: 'addon_peripheral_ds18b20',
};

const peripheralsPro3EMTypes = {
	digital_out: 'addon_peripheral_switch',
};

const peripheralsProAddonTypes = {
	...peripheralsPlusTypes,
	digital_out: 'addon_peripheral_switch',
};
